import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { getBackendPreferredLocale, putBackendPreferredLocale } from '../api/preferredLocale'
import { createDebug } from '../utils/debugLog'
import { normalizeLocale } from './supportedLocales'

const log = createDebug('dw:i18n:locale-sync')

const BACKEND_SYNC_MAX_ATTEMPTS = 5
const BACKEND_SYNC_BASE_RETRY_MS = 1000
const BACKEND_SYNC_MAX_RETRY_MS = 30_000
const BACKEND_SYNC_JITTER_RATIO = 0.25

export function getBackendLocaleSyncRetryDelayMs(
  attempt: number,
  random: () => number = Math.random,
): number {
  const boundedAttempt = Math.max(1, attempt)
  const exponentialDelay = BACKEND_SYNC_BASE_RETRY_MS * 2 ** (boundedAttempt - 1)
  const cappedDelay = Math.min(exponentialDelay, BACKEND_SYNC_MAX_RETRY_MS)
  const jitterMultiplier = 1 + (random() * 2 - 1) * BACKEND_SYNC_JITTER_RATIO
  const jitteredDelay = Math.round(cappedDelay * jitterMultiplier)
  return Math.min(jitteredDelay, BACKEND_SYNC_MAX_RETRY_MS)
}

/**
 * LocaleSync — mounted once inside <AuthProvider>.
 *
 * `public.user_preferred_locales` (the backend-owned table) is the single
 * source of truth for a user's persisted UI-locale preference. On authentication
 * this hook reconciles that table with the live UI:
 *
 *   - If the table has a stored locale, switch the UI to it (authoritative).
 *   - If the table has NO row yet (null), backfill it once from the current
 *     `i18n.language` — the pre-auth choice from the localStorage/navigator
 *     detector chain. We do NOT switch the UI in that case (there is nothing to
 *     switch to; the local guess already applied).
 *
 * This replaced the former GoTrue `user_metadata.preferred_locale` read/write:
 * `user_metadata` is no longer used as a persistence store (it is client-writable
 * and unreadable off-request; S1-1). Ongoing locale changes are written straight
 * to the table by the language picker (see ProfileModal).
 *
 * Also sets `document.documentElement.lang` for accessibility / SEO.
 */
export function LocaleSync(): null {
  const { user } = useAuth()
  const { i18n } = useTranslation()
  // Per-user reconcile guard: null (not yet run) / userId (done for that user).
  const reconciledUserRef = useRef<string | null>(null)
  const attemptRef = useRef<{ userId: string; attempts: number } | null>(null)
  // The user id whose reconcile is currently in flight (null = idle). Tracking
  // the id — not a bare boolean — lets a session switch (A→B) detect that the
  // in-flight run belongs to a stale user and re-trigger for the new one.
  const inFlightUserRef = useRef<string | null>(null)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [retryTick, setRetryTick] = useState(0)

  useEffect(() => {
    const userId = user?.id
    if (!userId) {
      // Signed out — reset all per-user state so the next user reconciles fresh.
      // A still-in-flight request for the previous user will no-op on completion
      // because its captured id no longer matches inFlightUserRef.
      reconciledUserRef.current = null
      attemptRef.current = null
      inFlightUserRef.current = null
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
      return
    }

    if (reconciledUserRef.current === userId) return

    // A reconcile is already running. If it belongs to THIS user, let it finish.
    // If it belongs to a different (stale) user, don't start a second one now —
    // that run's finally handler re-triggers this effect once it settles, at
    // which point inFlightUserRef is clear and we start fresh for this user.
    if (inFlightUserRef.current !== null) return

    const attemptsForUser =
      attemptRef.current?.userId === userId ? attemptRef.current.attempts : 0
    if (attemptsForUser >= BACKEND_SYNC_MAX_ATTEMPTS) return

    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }

    inFlightUserRef.current = userId

    // Guards async completion against the id this run started for, so a stale
    // run (user already switched) can neither apply a locale nor mutate the new
    // user's reconcile/attempt state.
    const isRunCurrent = () =>
      inFlightUserRef.current === userId && user?.id === userId

    const reconcile = async () => {
      const stored = await getBackendPreferredLocale()
      if (!isRunCurrent()) return

      if (stored) {
        // Table wins: switch the UI to the persisted preference.
        if (normalizeLocale(i18n.language) !== stored) {
          log({ event: 'locale-sync-from-table', from: i18n.language, to: stored })
          await i18n.changeLanguage(stored)
        }
      } else {
        // No stored row yet — backfill the table from the local pre-auth choice.
        // Do NOT change the UI: the local detector already applied it.
        const local = normalizeLocale(i18n.language)
        if (local) {
          log({ event: 'locale-backfill-table', locale: local, userId })
          await putBackendPreferredLocale(local)
        }
      }
    }

    reconcile()
      .then(() => {
        if (!isRunCurrent()) return
        reconciledUserRef.current = userId
        attemptRef.current = null
      })
      .catch((err: Error) => {
        if (!isRunCurrent()) return
        const message = err instanceof Error ? err.message : String(err)
        const nextAttempt = attemptsForUser + 1
        attemptRef.current = { userId, attempts: nextAttempt }
        log({ event: 'locale-sync-error', message, attempt: nextAttempt })
        if (nextAttempt >= BACKEND_SYNC_MAX_ATTEMPTS) {
          log({ event: 'locale-sync-abandoned', userId, attempts: nextAttempt })
          return
        }
        const delay = getBackendLocaleSyncRetryDelayMs(nextAttempt)
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null
          setRetryTick((tick) => tick + 1)
        }, delay)
      })
      .finally(() => {
        // Only the run that owns the in-flight slot clears it.
        if (inFlightUserRef.current !== userId) return
        inFlightUserRef.current = null
        // If the active user changed while this (now-stale) run was in flight,
        // re-trigger so the current user gets reconciled — otherwise they'd be
        // stuck behind a bail-out that never fires again.
        if (user?.id && user.id !== userId) {
          setRetryTick((tick) => tick + 1)
        }
      })
  }, [user?.id, i18n, retryTick])

  useEffect(() => {
    document.documentElement.lang = i18n.language
  }, [i18n.language])

  useEffect(() => {
    return () => {
      inFlightUserRef.current = null
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
      }
    }
  }, [])

  return null
}
